/**
 * CrudPageLayout — shared page container for all V2 CRUD pages.
 *
 * Provides: outer container + action toolbar + scrollable table area.
 * Also exports LoadingState and EmptyState sub-components.
 *
 * Extracted from V2Users, V2Roles, V2Sites, V2Views, V2Connectors,
 * and V2SummaryTemplates to eliminate ~30 duplicated lines per page.
 */

import { Box, CircularProgress } from "@mui/material";
import { crudPageContainerSx, crudToolbarSx, crudScrollAreaSx } from "./crudStyles";

// ---------------------------------------------------------------------------
// LoadingState — centered spinner with message
// ---------------------------------------------------------------------------

export function LoadingState({ message }: Readonly<{ message: string }>) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "200px",
        gap: "12px",
        color: "var(--text-secondary)",
      }}
    >
      <CircularProgress size={20} sx={{ color: "var(--accent)" }} />
      <span>{message}</span>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// EmptyState — centered message when no data
// ---------------------------------------------------------------------------

export function EmptyState({ message }: Readonly<{ message: string }>) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "200px",
        color: "var(--text-tertiary)",
        fontSize: "0.875rem",
      }}
    >
      {message}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CrudPageLayout
// ---------------------------------------------------------------------------

interface CrudPageLayoutProps {
  /** Content for the action toolbar row */
  readonly toolbar?: React.ReactNode;
  /** Main scrollable area (table, loading state, etc.) */
  readonly children: React.ReactNode;
  /** Extra content rendered after the scroll area (e.g. drawers, modals) */
  readonly extra?: React.ReactNode;
}

export default function CrudPageLayout({
  toolbar,
  children,
  extra,
}: CrudPageLayoutProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "var(--bg-deep)",
        position: "relative",
        padding: "12px",
        gap: "12px",
      }}
    >
      <Box sx={{ ...crudPageContainerSx, flex: 1, minWidth: 0 }}>
        {toolbar !== undefined && (
          <Box sx={crudToolbarSx}>
            {toolbar}
          </Box>
        )}

        <Box sx={crudScrollAreaSx}>
          {children}
        </Box>
      </Box>

      {extra}
    </Box>
  );
}
